import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckIcon, PhoneIcon } from 'lucide-react';
import { LandingHero } from '../components/LandingHero';
import { TrustLocalBand } from '../components/TrustLocalBand';
import { FaqList } from '../components/FaqList';
import { serviceOptionValue } from '../components/HeroLeadForm';
import { NotFound } from './NotFound';
import { findService } from '../data/serviceCategories';
import { business } from '../data/site';
import { buildServiceContent, relatedHeading } from '../utils/serviceContent';
import { usePageMeta } from '../hooks/usePageMeta';
export function ServiceDetail() {
  const {
    categorySlug,
    serviceSlug
  } = useParams();
  const match = findService(categorySlug, serviceSlug);
  const content = match ? buildServiceContent(match.category, match.service) : undefined;
  usePageMeta(content?.metaTitle ?? 'Services | East End Automotive Repair', content?.metaDescription ?? 'Auto repair services in Oak Ridge, TN from East End Automotive Repair.');
  if (!match || !content) return <NotFound data-id="element-697" data-mp="18f6ucxmnfvc7" />;
  const {
    category,
    service
  } = match;
  const related = category.services.filter(s => s.slug !== service.slug);
  return <>
      <LandingHero eyebrow="Oak Ridge, Tennessee" title={`${service.name} in Oak Ridge, TN`} intro={content.intro} crumbs={[{
      label: 'Services',
      to: '/services'
    }, {
      label: category.name,
      to: `/services/${category.slug}`
    }, {
      label: service.name
    }]} defaultService={serviceOptionValue(category.slug, service.slug)} sourcePage={`service-${category.slug}-${service.slug}`} data-id="element-698" data-mp="zfa0mhlek6aa" />

      <section className="w-full bg-white py-14 lg:py-20" data-id="element-699" data-mp="kr5ndt54cmyw">
        <div className="mx-auto max-w-shell px-5 lg:px-8" data-id="element-700" data-mp="krovyj8vpz09">
          <div className="grid gap-12 md:grid-cols-2 lg:gap-16" data-id="element-701" data-mp="6re7fmv1kwi9">
            <div data-id="element-702" data-mp="bqr0ipciazta">
              <h2 className="font-display text-[22px] font-extrabold leading-[1.15] tracking-[-0.02em] text-ink sm:text-[27px]" data-id="element-703" data-mp="br8t9bzymvwg">
                When this service may be needed
              </h2>
              <ul className="mt-5 space-y-2.5" data-id="element-704" data-mp="28oocjs3sx236">
                {content.whenNeeded.map(item => <li key={item} className="flex items-start gap-2.5 text-[15.5px] leading-snug text-steel/80" data-id="element-705" data-mp="23plif2pd0g9z">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" data-id="element-706" data-mp="1zmowb20m221a" />
                    {item}
                  </li>)}
              </ul>
              <p className="mt-5 max-w-[560px] text-[14px] leading-relaxed text-steel/60" data-id="element-707" data-mp="2ncrgkvyaa3pc">
                {content.situationNote}
              </p>
            </div>

            <div data-id="element-708" data-mp="1bzgq8hvcyoy7">
              <h2 className="font-display text-[22px] font-extrabold leading-[1.15] tracking-[-0.02em] text-ink sm:text-[27px]" data-id="element-709" data-mp="1bzysbv4pbxe1">
                What this service addresses
              </h2>
              <ul className="mt-5 space-y-3" data-id="element-710" data-mp="3inz7vs0quw0i">
                {content.whatWeCheck.map(item => <li key={item} className="flex items-start gap-2.5" data-id="element-711" data-mp="3nnc433di9ffr">
                    <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-dark" aria-hidden="true" data-id="element-712" data-mp="3ro4ehzy45qdm" />
                    <span className="text-[15.5px] leading-snug text-steel/80" data-id="element-713" data-mp="bb3do7rn2jf">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-chalk py-14 lg:py-20" data-id="element-714" data-mp="f67gofufmg3d">
        <div className="mx-auto max-w-shell px-5 lg:px-8" data-id="element-715" data-mp="f4mlco0amt4g">
          <h2 className="font-display text-[22px] font-extrabold leading-[1.15] tracking-[-0.02em] text-ink sm:text-[27px]" data-id="element-716" data-mp="j7j0g4qmk73n">
            What to expect at East End
          </h2>
          <ol className="mt-7 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-717" data-mp="2l0gyqg367rvz">
            {content.steps.map((step, i) => <li key={step.label} data-id="element-718" data-mp="1pno5y2j9l074" data-mpi={i}>
                <span aria-hidden="true" className="flex h-8 w-8 items-center justify-center rounded bg-ink font-display text-[13px] font-bold text-brand-light" data-id="element-719" data-mp="1lnnu8pnjv33r">
                  {i + 1}
                </span>
                <h3 className="mt-3 font-display text-[16px] font-bold tracking-tight text-ink" data-id="element-720" data-mp="1uovei3t8e3eu">
                  {step.label}
                </h3>
                <p className="mt-1.5 text-[14.5px] leading-relaxed text-steel/70" data-id="element-721" data-mp="11zya94luu753">
                  {step.copy}
                </p>
              </li>)}
          </ol>
          <p className="mt-7 max-w-[680px] text-[14.5px] leading-relaxed text-steel/65" data-id="element-722" data-mp="1gqdy6r5nnt6y">
            Findings and recommendations are explained before any work is
            approved. Read more about{' '}
            <Link to="/the-east-end-way" className="font-semibold text-brand-dark hover:underline" data-id="element-723" data-mp="j96qd948qycg">
              the East End Way
            </Link>
            .
          </p>
        </div>
      </section>

      {related.length > 0 && <section className="w-full bg-white py-14 lg:py-20" data-id="element-724" data-mp="2zpbu5ani6owa">
          <div className="mx-auto max-w-shell px-5 lg:px-8" data-id="element-725" data-mp="2lwvoe237wcro">
            <h2 className="font-display text-[22px] font-extrabold leading-[1.15] tracking-[-0.02em] text-ink sm:text-[27px]" data-id="element-726" data-mp="2px62y352sbi5">
              {relatedHeading(category.name)}
            </h2>
            <ul className="mt-6 flex flex-wrap gap-2" data-id="element-727" data-mp="3im4lh1d41fnz">
              {related.map(s => <li key={s.slug} data-id="element-728" data-mp="3ijqsqz5wkfjl">
                  <Link to={`/services/${category.slug}/${s.slug}`} className="inline-block rounded border border-black/10 bg-chalk px-3 py-2 text-[13px] leading-snug text-steel/80 transition-colors duration-150 ease-out hover:border-ink/40 hover:text-ink" data-id="element-729" data-mp="34n4xzx1c36te">
                    {s.name}
                  </Link>
                </li>)}
            </ul>
            <Link to={`/services/${category.slug}`} className="mt-7 inline-block text-[12.5px] font-bold uppercase tracking-[0.08em] text-brand-dark hover:underline" data-id="element-730" data-mp="3oq6pzc2a5yd">
              All {category.name}
            </Link>
          </div>
        </section>}

      <TrustLocalBand data-id="element-731" data-mp="3m3917vv64hqe" />

      <FaqList faqs={content.faqs} heading={`${service.name} questions`} data-id="element-732" data-mp="3m1y063t3xo2z" />

      <section className="w-full bg-ink py-14 text-white lg:py-16" data-id="element-733" data-mp="3h6iqlo24uecd">
        <div className="mx-auto flex max-w-shell flex-col gap-7 px-5 lg:flex-row lg:items-center lg:justify-between lg:px-8" data-id="element-734" data-mp="3c5l8fbh4hkav">
          <div data-id="element-735" data-mp="3779zd2ynjdtp">
            <h2 className="max-w-[600px] font-display text-[26px] font-extrabold leading-[1.1] tracking-[-0.02em] sm:text-[32px]" data-id="element-736" data-mp="3771ikzzerod8">
              Talk to East End about {service.name.toLowerCase()} in Oak Ridge,
              TN
            </h2>
            <p className="mt-4 max-w-[520px] text-[16px] leading-relaxed text-chalk/70" data-id="element-737" data-mp="11da128kd7cyc">
              Call the shop during business hours and describe what your vehicle
              is doing.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center lg:shrink-0" data-id="element-738" data-mp="2o985wtkquli2">
            <a href={business.phoneHref} className="inline-flex items-center justify-center gap-3 whitespace-nowrap rounded-md bg-brand px-7 py-4 font-display text-lg font-bold text-white transition-colors duration-150 ease-out hover:bg-brand-light" data-id="element-739" data-mp="2t2apq9zpygl2">
              <PhoneIcon className="h-5 w-5 shrink-0" aria-hidden="true" data-id="element-740" data-mp="25avko9gk5l64" />
              Call {business.phone}
            </a>
            <Link to="/contact" className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-white/25 px-7 py-4 text-base font-semibold text-white transition-colors duration-150 ease-out hover:border-white/60" data-id="element-741" data-mp="29bp7ezi9ktxf">
              Contact the Shop
            </Link>
          </div>
        </div>
      </section>
    </>;
}